import { GraphComponent, ILabel, INode, Rect } from '@ardoq/yfiles';
import {
  NodeExportSupport,
  LabelExportSupport,
  ComponentGraphGroup,
  GraphItem,
} from '@ardoq/graph';
import { GroupStyle } from 'tabview/blockDiagram/view/yFilesExtensions/modernized/GroupStyle';
import { CollapsedComponentGroupLabelStyle } from 'tabview/blockDiagram/view/yFilesExtensions/modernized/CollapsedComponentGroupLabelStyle';
import {
  calcCollapsedGroupSubShapeLayout,
  calcCollapsedGroupLabelLayout,
} from './collapsedGroupGeometry';
import { collapsedComponentGroupShapeY } from 'yfilesExtensions/styles/modernized/leafNode/collapsedComponentGroupShapeY';
import { wrapString } from 'tabview/blocks/misc/string';
import { MODERNIZED_BLOCK_DIAGRAM_LABEL_MAX_LINES as MAX_LINES } from 'yfilesExtensions/styles/modernized/consts';
import { measureNodeLabel } from '@ardoq/graph-labeling';
import { MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_LABEL_WIDTH as LABEL_WIDTH } from 'tabview/blockDiagram/view/yFilesExtensions/modernized/consts';
import { collapsedComponentGroupLabelTop } from 'tabview/blockDiagram/view/yFilesExtensions/modernized/leafNodeLabel/collapsedComponentGroupLabelTop';
import {
  createLabelStyle,
  Preset,
} from 'tabview/blockDiagram/view/yFilesExtensions/shared/styleUtils';

const getGroupSubShapeLayout = (
  _gc: GraphComponent,
  node: INode
): Rect | null => {
  const graphGroup = node.tag as ComponentGraphGroup | null;
  if (!graphGroup) {
    return null;
  }
  if (!graphGroup.isComponent() || !graphGroup.collapsed) {
    return null;
  }
  const { x, y, width: nodeWidth } = node.layout;
  const shapeY = collapsedComponentGroupShapeY(node);
  const layout = calcCollapsedGroupSubShapeLayout(x, y, nodeWidth, shapeY);
  return new Rect(layout.x, layout.y, layout.width, layout.height);
};

const toCollapsedGroupLabelJSON = (_gc: GraphComponent, label: ILabel) => {
  const { owner, text } = label;
  if (!(owner instanceof INode)) {
    return { text: '', layout: { x: 0, y: 0, width: 0, height: 0 } };
  }
  const { isTransparentized } = owner.tag as GraphItem;
  const wrappedLabel = wrapString(
    text,
    LABEL_WIDTH,
    measureNodeLabel,
    MAX_LINES
  );
  const labelTop = collapsedComponentGroupLabelTop(owner);
  const layout = calcCollapsedGroupLabelLayout(
    owner.layout.center.x,
    labelTop,
    wrappedLabel.length
  );
  const style = createLabelStyle(
    null,
    { kind: 'lines', lines: wrappedLabel },
    { preset: Preset.Modern, isTransparentized }
  );
  return { text, layout, style: style ?? undefined };
};

export const applyExportDecorators = (graphComponent: GraphComponent): void => {
  const graph = graphComponent.graph;

  graph.decorator.nodes.getDecoratorFor(NodeExportSupport).addFactory(
    (node: INode) => node.style instanceof GroupStyle,
    () =>
      ({
        getSubShapeLayout: getGroupSubShapeLayout,
      }) satisfies NodeExportSupport
  );

  graph.decorator.labels.getDecoratorFor(LabelExportSupport).addFactory(
    (label: ILabel) =>
      label.style === CollapsedComponentGroupLabelStyle.Instance,
    () => ({ toJSON: toCollapsedGroupLabelJSON }) satisfies LabelExportSupport
  );
};
